import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HiShieldCheck, HiXMark } from 'react-icons/hi2';
import { sendPasswordResetOtp, verifyPasswordResetOtpOnly } from '../../services/settingsService';
import toast from 'react-hot-toast';

const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const modalVariants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
    y: 10,
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 10,
  },
};

/**
 * Popover 1: OTP Input Prompt
 * This is the first step in the two-step password reset flow.
 * User enters OTP code sent to their email.
 */
export default function PasswordResetOtpInputPrompt({ email, open, onOtpVerified, onCancel }) {
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [otpRequestId, setOtpRequestId] = useState(null);
  const [expiryMinutes, setExpiryMinutes] = useState(10);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const hasRequestedOtp = useRef(false);

  useEffect(() => {
    if (!open || !email) {
      hasRequestedOtp.current = false;
      return;
    }

    if (hasRequestedOtp.current) return;

    let isMounted = true;

    const initOtp = async () => {
      try {
        setInitializing(true);
        hasRequestedOtp.current = true;
        setError('');

        // Normalize email before sending to match backend expectation
        const normalizedEmail = email?.toLowerCase().trim();
        if (!normalizedEmail) {
          throw new Error('Email is required');
        }

        const start = await sendPasswordResetOtp(normalizedEmail);
        if (!isMounted) return;

        if (!start.otpRequestId) {
          throw new Error('Failed to receive OTP request ID');
        }

        setOtpRequestId(start.otpRequestId);
        setExpiryMinutes(start.expiryMinutes || 10);
        setCode('');
        setError('');
        // Show success message when OTP is sent
        toast.success('Password reset OTP sent to your email. Please check your inbox.');
      } catch (e) {
        if (!isMounted) return;
        hasRequestedOtp.current = false;
        let errorMsg = 'Unable to send password reset OTP. Please try again.';

        if (e?.code === 'not-found') {
          errorMsg = 'Email address not found in the system.';
        } else if (e?.message) {
          errorMsg = e.message;
        }

        toast.error(errorMsg);
        setError(errorMsg);
      } finally {
        if (isMounted) setInitializing(false);
      }
    };

    initOtp();

    return () => {
      isMounted = false;
    };
  }, [open, email]);

  if (!open) return null;

  const handleVerify = async e => {
    e.preventDefault();
    if (!otpRequestId || code.length !== 6) {
      setError('Please enter the 6-digit code sent to your email.');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Normalize email before sending to match backend expectation
      const normalizedEmail = email?.toLowerCase().trim();

      const res = await verifyPasswordResetOtpOnly({
        otpRequestId,
        email: normalizedEmail,
        otp: code,
      });

      if (res.success) {
        toast.success('OTP verified successfully. Please set your new password.');
        if (onOtpVerified) {
          onOtpVerified({ otpRequestId, email: normalizedEmail, otp: code });
        }
      } else {
        setError('OTP verification failed. Please try again.');
      }
    } catch (err) {
      // Handle specific error codes from backend
      let errorMessage = 'OTP verification failed. Please try again.';

      if (err?.code === 'deadline-exceeded' || err?.message?.includes('expired')) {
        errorMessage = 'OTP has expired. Please request a new code.';
      } else if (err?.code === 'invalid-argument' || err?.message?.includes('Invalid')) {
        errorMessage = 'Invalid or expired OTP. Please try again.';
      } else if (err?.code === 'failed-precondition') {
        errorMessage = err?.message || 'OTP has already been used or is invalid.';
      } else if (err?.code === 'resource-exhausted') {
        errorMessage = 'Too many incorrect attempts. Please request a new OTP.';
      } else if (err?.code === 'not-found') {
        errorMessage = 'OTP request not found. Please request a new code.';
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email) return;
    try {
      setLoading(true);
      setError('');
      hasRequestedOtp.current = false;

      // Normalize email before sending to match backend expectation
      const normalizedEmail = email?.toLowerCase().trim();
      if (!normalizedEmail) {
        throw new Error('Email is required');
      }

      const start = await sendPasswordResetOtp(normalizedEmail);

      if (!start.otpRequestId) {
        throw new Error('Failed to receive OTP request ID');
      }

      hasRequestedOtp.current = true;
      setOtpRequestId(start.otpRequestId);
      setExpiryMinutes(start.expiryMinutes || 10);
      setCode('');
      toast.success('A new OTP has been sent to your email.');
    } catch (e) {
      hasRequestedOtp.current = false;
      let errorMsg = 'Failed to resend OTP. Please try again.';

      if (e?.code === 'not-found') {
        errorMsg = 'Email address not found in the system.';
      } else if (e?.message) {
        errorMsg = e.message;
      }

      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading && onCancel) onCancel();
  };

  return (
    <AnimatePresence>
      <motion.div
        key="otp-input-backdrop"
        variants={backdropVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        transition={{ duration: 0.15, ease: 'easeOut' }}
        className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4"
      >
        <motion.div
          key="otp-input-modal"
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="relative w-[92%] max-w-md rounded-2xl bg-white p-6 shadow-2xl sm:w-full"
        >
          <button
            type="button"
            onClick={handleClose}
            className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <HiXMark className="h-5 w-5" />
          </button>

          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600 text-white">
              <HiShieldCheck className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Verify Your Email</h2>
              <p className="text-xs text-gray-600">
                Enter the OTP sent to <span className="font-medium">{email}</span>
              </p>
            </div>
          </div>

          {initializing ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
            </div>
          ) : (
            <form onSubmit={handleVerify} className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  One-Time Password
                </label>
                <input
                  type="text"
                  value={code}
                  onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                  className="w-full rounded-lg border-2 border-gray-300 px-4 py-3 text-center text-2xl font-bold tracking-widest text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10"
                  placeholder="000000"
                />
              </div>

              {error && <p className="text-xs text-red-600">{error}</p>}
              <p className="text-xs text-gray-500">
                Code expires in approximately {expiryMinutes} minutes.
              </p>

              <div className="flex items-center justify-between pt-2">
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={loading}
                  className="text-xs font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50"
                >
                  Resend code
                </button>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="rounded-lg border border-gray-300 px-4 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading || code.length !== 6}
                    className="rounded-lg bg-blue-600 px-4 py-2 text-xs font-semibold text-white shadow-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? 'Verifying…' : 'Verify OTP'}
                  </button>
                </div>
              </div>
            </form>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
